import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightIcon,
    BuildingOffice2Icon,
    MapPinIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import Hero from "../components/Hero";
import destImg from "../assests/images/hotel-bg.jpg";

export default function DestinationsList({
    showBanner = true,
    showHeading = true,
}) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    // =========================================================
    // FETCH DESTINATIONS
    // =========================================================

    const fetchDestinations = async () => {
        setLoading(true);

        try {
            const res = await axios.get(
                "https://cheapdubaihotels.com/api/destinations"
            );

            setDestinations(res.data);
        } catch (err) {
            console.error("Failed to fetch destinations:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    return (
        <>
            <div className="w-full bg-[#F4F7F7]">

                {/* =========================================================
                HERO
            ========================================================= */}

                {showBanner && (
                    <Hero
                        variant="inner"
                        title="Explore Dubai"
                        subtitle="Discover the city's most desirable destinations and find the perfect hotel for your Dubai stay."
                        backgroundImage={destImg}
                    />
                )}

                {/* =========================================================
                MAIN CONTENT
            ========================================================= */}

                <section
                    className="
                    relative
                    overflow-hidden
                    bg-[#F4F7F7]
                    py-14
                    sm:py-16
                    lg:py-20
                    xl:py-24
                "
                >

                    {/* =====================================================
                    DECORATIVE BACKGROUND
                ===================================================== */}

                    <div
                        className="
                        pointer-events-none
                        absolute
                        -left-40
                        top-20
                        h-96
                        w-96
                        rounded-full
                        bg-[#123F42]/[0.05]
                        blur-3xl
                    "
                    />

                    <div
                        className="
                        pointer-events-none
                        absolute
                        -right-40
                        top-[45%]
                        h-96
                        w-96
                        rounded-full
                        bg-[#8B0000]/[0.035]
                        blur-3xl
                    "
                    />

                    <div
                        className="
                        pointer-events-none
                        absolute
                        bottom-0
                        left-1/2
                        h-72
                        w-72
                        -translate-x-1/2
                        rounded-full
                        bg-[#D7A74A]/[0.05]
                        blur-3xl
                    "
                    />

                    <div
                        className="
                        relative
                        z-10
                        mx-auto
                        max-w-[1440px]
                        px-5
                        sm:px-8
                        lg:px-12
                        xl:px-16
                    "
                    >

                        {/* =================================================
                        SECTION INTRO
                    ================================================= */}

                        {showHeading && (
                            <div
                                className="
                                mb-10
                                flex
                                flex-col
                                gap-7
                                lg:mb-12
                                lg:flex-row
                                lg:items-end
                                lg:justify-between
                            "
                            >

                                {/* LEFT */}

                                <div className="max-w-[700px]">

                                    {/* Eyebrow */}

                                    <div
                                        className="
                                        mb-4
                                        flex
                                        items-center
                                        gap-3
                                    "
                                    >
                                        <span
                                            className="
                                            h-[1px]
                                            w-9
                                            bg-[#8B0000]
                                        "
                                        />

                                        <span
                                            className="
                                            text-[10px]
                                            font-semibold
                                            uppercase
                                            tracking-[0.24em]
                                            text-[#8B0000]
                                        "
                                        >
                                            Discover Dubai
                                        </span>

                                        <span className="text-[#D7A74A]">
                                            •
                                        </span>

                                        <span
                                            className="
                                            text-[10px]
                                            font-medium
                                            uppercase
                                            tracking-[0.18em]
                                            text-[#64747A]
                                        "
                                        >
                                            Find Your Stay
                                        </span>
                                    </div>

                                    {/* Heading */}

                                    <h2
                                        className="
                                        font-serif
                                        text-[32px]
                                        font-medium
                                        leading-[1.08]
                                        tracking-[-0.025em]
                                        text-[#123F42]

                                        sm:text-[38px]

                                        lg:text-[46px]
                                    "
                                    >
                                        Explore Dubai's most
                                        <span
                                            className="
                                            block
                                            italic
                                            text-[#8B0000]
                                        "
                                        >
                                            beautiful destinations.
                                        </span>
                                    </h2>

                                    {/* Description */}

                                    <p
                                        className="
                                        mt-5
                                        max-w-[620px]
                                        text-[13px]
                                        leading-6
                                        text-[#68777A]

                                        sm:text-[14px]
                                        sm:leading-7
                                    "
                                    >
                                        From iconic beachfront escapes to vibrant
                                        city districts, discover the areas that
                                        make Dubai unforgettable and find a hotel
                                        that fits the way you want to travel.
                                    </p>
                                </div>

                                {/* DESTINATION COUNT */}

                                {!loading && destinations.length > 0 && (
                                    <div className="shrink-0">
                                        <div
                                            className="
                                            flex
                                            items-center
                                            gap-4
                                            rounded-[20px]
                                            border
                                            border-[#DDE5E5]
                                            bg-white
                                            px-5
                                            py-4
                                            shadow-[0_12px_35px_rgba(18,63,66,0.08)]
                                        "
                                        >
                                            <div
                                                className="
                                                flex
                                                h-12
                                                w-12
                                                items-center
                                                justify-center
                                                rounded-full
                                                bg-[#123F42]
                                                text-white
                                                shadow-md
                                            "
                                            >
                                                <MapPinIcon className="h-5 w-5" />
                                            </div>

                                            <div>
                                                <p
                                                    className="
                                                    font-serif
                                                    text-2xl
                                                    font-medium
                                                    leading-none
                                                    text-[#123F42]
                                                "
                                                >
                                                    {destinations.length}
                                                </p>

                                                <p
                                                    className="
                                                    mt-1
                                                    text-[9px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-[0.18em]
                                                    text-[#7A898E]
                                                "
                                                >
                                                    Dubai Destinations
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* =================================================
                        PREMIUM DIVIDER
                    ================================================= */}

                        {showHeading && (
                            <div className="mb-8 flex items-center gap-3">
                                <span className="h-[1px] w-12 bg-[#8B0000]" />

                                <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />

                                <span className="h-[1px] flex-1 bg-[#DDE5E5]" />
                            </div>
                        )}

                        {/* =================================================
                        LOADING
                    ================================================= */}

                        {loading ? (
                            <div
                                className="
                                grid
                                grid-cols-1
                                gap-5
                                sm:grid-cols-2
                                lg:grid-cols-12
                            "
                            >
                                <div
                                    className="
                                    h-[390px]
                                    animate-pulse
                                    rounded-[24px]
                                    bg-[#DDE5E5]
                                    sm:col-span-2
                                    lg:col-span-7
                                "
                                />

                                {[1, 2, 3, 4, 5].map((item) => (
                                    <div
                                        key={item}
                                        className="
                                        h-[220px]
                                        animate-pulse
                                        rounded-[24px]
                                        bg-[#DDE5E5]
                                        lg:col-span-5
                                    "
                                    />
                                ))}
                            </div>

                        ) : destinations.length === 0 ? (

                            /* =================================================
                               EMPTY STATE
                            ================================================= */

                            <div
                                className="
                                rounded-[24px]
                                border
                                border-[#DDE5E5]
                                bg-white
                                px-6
                                py-20
                                text-center
                                shadow-[0_15px_45px_rgba(18,63,66,0.07)]
                            "
                            >
                                <div
                                    className="
                                    mx-auto
                                    flex
                                    h-16
                                    w-16
                                    items-center
                                    justify-center
                                    rounded-full
                                    bg-[#123F42]
                                    text-white
                                    shadow-lg
                                "
                                >
                                    <MapPinIcon className="h-7 w-7" />
                                </div>

                                <h3
                                    className="
                                    mt-6
                                    font-serif
                                    text-2xl
                                    font-medium
                                    text-[#123F42]
                                "
                                >
                                    No destinations available
                                </h3>

                                <p
                                    className="
                                    mx-auto
                                    mt-2
                                    max-w-md
                                    text-[13px]
                                    leading-6
                                    text-[#68777A]
                                "
                                >
                                    We couldn't find any destinations right now.
                                    Please check again shortly.
                                </p>
                            </div>

                        ) : (

                            /* =================================================
                               DESTINATION GRID
                            ================================================= */




                            < div className="
                        grid
                        grid-cols-1
                        gap-5
                        sm:grid-cols-2
                        lg:grid-cols-3
                        lg:gap-6
">
                                {destinations.map((dest, index) => {
                                    const isFeatured = index === 0;

                                    return (
                                        <div
                                            key={dest.id}
                                            onClick={() =>
                                                navigate(`/destinations/${dest.slug}`)
                                            }
                                            className={`
                    group
                    relative
                    isolate
                    cursor-pointer
                    overflow-hidden
                    rounded-[20px]
                    bg-[#123F42]
                    shadow-[0_8px_30px_rgba(18,63,66,0.08)]
                    transition-all
                    duration-500
                    hover:-translate-y-1
                    hover:shadow-[0_20px_45px_rgba(18,63,66,0.16)]

                    ${isFeatured
                                                    ? "h-[380px] sm:col-span-2 sm:h-[420px] lg:h-[440px]"
                                                    : "h-[300px] sm:h-[320px] lg:h-[440px]"
                                                }
                `}
                                        >

                                            {/* DESTINATION IMAGE */}

                                            <img
                                                src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                                alt={dest.name}
                                                loading={index > 2 ? "lazy" : "eager"}
                                                className="
                        absolute
                        inset-0
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-700
                        ease-out
                        group-hover:scale-105
                    "
                                            />

                                            {/* IMAGE OVERLAY */}

                                            <div className="
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-[#071F21]/95
                    via-[#071F21]/25
                    to-black/10
                    transition-opacity
                    duration-500
                    group-hover:from-[#071F21]/90
                " />

                                            {/* TOP BADGES */}

                                            <div className="
                    absolute
                    left-4
                    right-4
                    top-4
                    z-10
                    flex
                    items-center
                    justify-between
                    sm:left-5
                    sm:right-5
                    sm:top-5
                ">

                                                {/* DESTINATION NUMBER */}

                                                <span className="
                        flex
                        h-9
                        min-w-9
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-white/30
                        bg-black/20
                        px-2.5
                        text-[11px]
                        font-medium
                        tracking-wide
                        text-white
                        backdrop-blur-md
                    ">
                                                    {String(index + 1).padStart(2, "0")}
                                                </span>

                                                {/* HOTEL COUNT */}

                                                <div className="
                        flex
                        items-center
                        gap-2
                        rounded-full
                        border
                        border-white/25
                        bg-white/15
                        px-3.5
                        py-2
                        backdrop-blur-md
                    ">
                                                    <BuildingOffice2Icon className="
                            h-4
                            w-4
                            text-[#E8C77B]
                        " />

                                                    <span className="
                            text-[11px]
                            font-medium
                            text-white
                        ">
                                                        {dest.hotels_count || 0} Hotels
                                                    </span>
                                                </div>
                                            </div>

                                            {/* CARD CONTENT */}

                                            <div className="
                    absolute
                    inset-x-0
                    bottom-0
                    z-10
                    p-5
                    sm:p-6
                    lg:p-7
                ">

                                                {/* LOCATION LABEL */}

                                                <div className="
                        mb-3
                        flex
                        items-center
                        gap-2
                    ">
                                                    <MapPinIcon className="
                            h-3.5
                            w-3.5
                            text-[#E8C77B]
                        " />

                                                    <span className="
                            text-[10px]
                            font-semibold
                            uppercase
                            tracking-[0.18em]
                            text-white/80
                        ">
                                                        Dubai, UAE
                                                    </span>
                                                </div>

                                                {/* TITLE + ARROW */}

                                                <div className="
                        flex
                        items-end
                        justify-between
                        gap-3
                    ">

                                                    <div className="min-w-0">

                                                        <h3 className={`
                                font-serif
                                font-medium
                                leading-tight
                                tracking-[-0.02em]
                                text-white
                                ${isFeatured
                                                                ? "text-3xl sm:text-4xl lg:text-[42px]"
                                                                : "text-2xl sm:text-[26px]"
                                                            }
                            `}>
                                                            {dest.name}
                                                        </h3>

                                                        <p className="
                                mt-2
                                text-xs
                                leading-5
                                text-white/70
                            ">
                                                            Discover hotels and beautiful stays
                                                        </p>

                                                    </div>

                                                    {/* ARROW BUTTON */}

                                                    <div className="
                            flex
                            h-11
                            w-11
                            shrink-0
                            items-center
                            justify-center
                            rounded-full
                            bg-white
                            text-[#123F42]
                            transition-all
                            duration-300
                            group-hover:rotate-[-45deg]
                            group-hover:bg-[#D7A74A]
                        ">
                                                        <ArrowRightIcon className="
                                h-5
                                w-5
                            " />
                                                    </div>

                                                </div>

                                                {/* BOTTOM ACCENT */}

                                                <div className="
                        mt-5
                        h-[2px]
                        w-full
                        overflow-hidden
                        rounded-full
                        bg-white/20
                    ">
                                                    <div className="
                            h-full
                            w-0
                            rounded-full
                            bg-[#D7A74A]
                            transition-all
                            duration-500
                            group-hover:w-full
                        " />
                                                </div>

                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* =================================================
                        BOTTOM CTA
                    ================================================= */}

                        {!loading && destinations.length > 0 && (
                            <div
                                className="
                                relative
                                mt-10
                                overflow-hidden
                                rounded-[26px]
                                bg-[#123F42]
                                shadow-[0_20px_50px_rgba(18,63,66,0.16)]
                            "
                            >

                                {/* Decorative glow */}

                                <div
                                    className="
                                    pointer-events-none
                                    absolute
                                    -right-20
                                    -top-32
                                    h-72
                                    w-72
                                    rounded-full
                                    bg-[#8B0000]/20
                                    blur-3xl
                                "
                                />

                                <div
                                    className="
                                    pointer-events-none
                                    absolute
                                    -bottom-32
                                    left-1/3
                                    h-64
                                    w-64
                                    rounded-full
                                    bg-[#D7A74A]/10
                                    blur-3xl
                                "
                                />

                                <div
                                    className="
                                    relative
                                    flex
                                    flex-col
                                    gap-6
                                    px-6
                                    py-7
                                    sm:px-8
                                    sm:py-8
                                    md:flex-row
                                    md:items-center
                                    md:justify-between
                                    lg:px-10
                                "
                                >

                                    {/* LEFT */}

                                    <div
                                        className="
                                        flex
                                        items-center
                                        gap-4
                                    "
                                    >
                                        <div
                                            className="
                                            hidden
                                            h-12
                                            w-12
                                            shrink-0
                                            items-center
                                            justify-center
                                            rounded-full
                                            border
                                            border-white/15
                                            bg-white/10
                                            text-[#D7A74A]
                                            sm:flex
                                        "
                                        >
                                            <SparklesIcon className="h-5 w-5" />
                                        </div>

                                        <div>
                                            <div
                                                className="
                                                mb-1
                                                text-[9px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.2em]
                                                text-[#D7A74A]
                                            "
                                            >
                                                Your Dubai Stay Starts Here
                                            </div>

                                            <h3
                                                className="
                                                font-serif
                                                text-xl
                                                font-medium
                                                text-white
                                                sm:text-2xl
                                            "
                                            >
                                                Ready to explore Dubai?
                                            </h3>

                                            <p
                                                className="
                                                mt-1
                                                text-[11px]
                                                leading-5
                                                text-white/65
                                                sm:text-xs
                                            "
                                            >
                                                Find the perfect hotel for your
                                                next Dubai stay.
                                            </p>
                                        </div>
                                    </div>

                                    {/* BUTTON */}

                                    <button
                                        type="button"
                                        onClick={() => navigate("/hotels")}
                                        className="
                                        group
                                        inline-flex
                                        items-center
                                        justify-center
                                        gap-3
                                        rounded-full
                                        bg-[#8B0000]
                                        px-7
                                        py-3.5
                                        text-[11px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.1em]
                                        text-white
                                        shadow-[0_10px_25px_rgba(0,0,0,0.20)]
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:bg-[#A00000]
                                        hover:shadow-[0_15px_30px_rgba(139,0,0,0.30)]
                                    "
                                    >
                                        Browse Hotels

                                        <ArrowRightIcon
                                            className="
                                            h-4
                                            w-4
                                            transition-transform
                                            duration-300
                                            group-hover:translate-x-1
                                        "
                                        />
                                    </button>
                                </div>

                                {/* GOLD ACCENT */}

                                <div
                                    className="
                                    h-[2px]
                                    w-full
                                    bg-gradient-to-r
                                    from-transparent
                                    via-[#D7A74A]
                                    to-transparent
                                "
                                />
                            </div>
                        )}

                        {/* =================================================
                        BRAND MESSAGE
                    ================================================= */}

                        {!loading && destinations.length > 0 && (
                            <div
                                className="
                                mt-8
                                flex
                                items-center
                                justify-center
                                gap-3
                                text-center
                            "
                            >
                                <span
                                    className="
                                    h-1.5
                                    w-1.5
                                    rounded-full
                                    bg-[#8B0000]
                                "
                                />

                                <span
                                    className="
                                    text-[9px]
                                    font-medium
                                    uppercase
                                    tracking-[0.2em]
                                    text-[#7A898E]
                                "
                                >
                                    Discover Dubai · Stay beautifully · Travel
                                    confidently
                                </span>

                                <span
                                    className="
                                    h-1.5
                                    w-1.5
                                    rounded-full
                                    bg-[#D7A74A]
                                "
                                />
                            </div>
                        )}
                    </div>
                </section >

            </div >
        </>
    );
}